import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import Tooltip from '../Tooltip';
import { ErrorIcon, InfoIcon } from '../Icon';
import { RequiredStateText } from '../RequiredStateText';
import { Calendar } from './Calendar';
import './DatePicker.css';

export interface DatePickerProps {
  /**
   * Label text for the date picker field
   */
  label: string;
  /**
   * Hint text to display below the label
   */
  hint?: string;
  /**
   * Placeholder text for the input
   * @default 'MM/DD/YYYY'
   */
  placeholder?: string;
  /**
   * Error message to display (overrides validation errors)
   */
  errorMessage?: string;
  /**
   * Minimum selectable date
   */
  fromDate?: Date;
  /**
   * Maximum selectable date
   */
  toDate?: Date;
  /**
   * Default date value for uncontrolled mode
   */
  defaultValue?: Date | string;
  /**
   * Controlled date value
   */
  value?: Date | string;
  /**
   * Whether the date picker is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the field is required
   * @default false
   */
  required?: boolean;
  /**
   * Callback when the date changes
   */
  onDateChange?: (date: Date | null, formattedDate: string) => void;
  /**
   * Callback when the date picker loses focus
   */
  onBlur?: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Name attribute for the input
   */
  name?: string;
  /**
   * ID attribute for the input
   */
  id?: string;
  /**
   * Custom error message for required validation
   * @default 'You must enter in a date using the MM/DD/YYYY format'
   */
  requiredErrorMessage?: string;
  /**
   * Whether to validate on blur
   * @default true
   */
  validateOnBlur?: boolean;
  /**
   * Tooltip info text to display next to the label
   */
  tooltipText?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  hint,
  placeholder = 'MM/DD/YYYY',
  errorMessage: externalErrorMessage,
  fromDate,
  toDate,
  defaultValue,
  value: controlledValue,
  disabled = false,
  required = false,
  onDateChange,
  onBlur,
  className,
  name,
  id,
  requiredErrorMessage = 'You must enter in a date using the MM/DD/YYYY format',
  validateOnBlur = true,
  tooltipText,
  'data-testid': dataTestId,
}) => {
  // ============================================================================
  // ID Generation & Configuration
  // ============================================================================
  const generatedId = useId();
  const stableName = useMemo(() => name || id || `date-picker-${generatedId}`, [name, id, generatedId]);
  const inputId = useMemo(() => id || stableName, [id, stableName]);
  const calendarId = useMemo(() => `${inputId}-calendar`, [inputId]);
  const tooltipId = useMemo(() => `${inputId}-tooltip`, [inputId]);
  // Names the input from the label text alone. A native <label for> contributes its
  // whole subtree, which welded the required asterisk and the tooltip trigger onto the
  // announced name ("Date of Birthrequired...").
  const labelTextId = useMemo(() => `${inputId}-label-text`, [inputId]);
  const requiredTextId = useMemo(() => `${inputId}-required`, [inputId]);
  const errorTooltipId = useMemo(() => `${inputId}-error-tooltip`, [inputId]);
  const baseTestId = dataTestId ?? 'date-picker';

  // ============================================================================
  // Refs
  // ============================================================================
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const previousInputValueRef = useRef<string>('');
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const restoreFocusTargetRef = useRef<'button' | 'input' | null>(null);

  // ============================================================================
  // Controlled vs Uncontrolled Mode
  // ============================================================================
  const isControlled = controlledValue !== undefined;

  const [uncontrolledDate, setUncontrolledDate] = useState<Date | null>(() => {
    if (defaultValue) {
      const initialDate = typeof defaultValue === 'string' ? new Date(defaultValue) : defaultValue;

      return initialDate;
    }

    return null;
  });

  // ============================================================================
  // Date Formatting & Parsing Utilities
  // ============================================================================
  const formatDateValue = useCallback((date: Date | null): string => {
    if (!date || isNaN(date.getTime())) return '';
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  }, []);

  const parseDateString = useCallback((dateString: string): Date | null => {
    if (!dateString.trim()) return null;

    // Only parse MM/DD/YYYY format - don't try to parse other formats
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);

      if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
        const parsedDate = new Date(year, month, day);
        if (
          !isNaN(parsedDate.getTime()) &&
          parsedDate.getMonth() === month &&
          parsedDate.getDate() === day &&
          parsedDate.getFullYear() === year
        ) {
          return parsedDate;
        }
      }
    }

    // Don't try to parse as ISO date - it's too permissive and causes issues
    // with incomplete input like "1" being parsed as "Jan 1, 2001"
    return null;
  }, []);

  /**
   * Formats user input as they type by automatically inserting slashes
   * User only needs to type numbers: 01152024 becomes 01/15/2024
   */
  const formatDateInput = useCallback((value: string, previousValue: string): string => {
    // Strip out all non-numeric characters
    const digitsOnly = value.replace(/\D/g, '');
    // If user is deleting (new value is shorter), allow it
    const isDeleting = value.length < previousValue.length;
    if (isDeleting) {
      // If they deleted a slash, also delete the digit before it
      if (previousValue.endsWith('/') && !value.endsWith('/')) {
        const digitsBeforeSlash = digitsOnly.slice(0, -1);
        if (digitsBeforeSlash.length === 0) return '';
        if (digitsBeforeSlash.length <= 2) return digitsBeforeSlash;
        if (digitsBeforeSlash.length <= 4) {
          return `${digitsBeforeSlash.slice(0, 2)}/${digitsBeforeSlash.slice(2)}`;
        }

        return `${digitsBeforeSlash.slice(0, 2)}/${digitsBeforeSlash.slice(2, 4)}/${digitsBeforeSlash.slice(4)}`;
      }
    }

    // Limit to 8 digits (MMDDYYYY)
    const limitedDigits = digitsOnly.slice(0, 8);

    // Format with slashes as user types
    if (limitedDigits.length === 0) return '';
    if (limitedDigits.length <= 2) return limitedDigits;
    if (limitedDigits.length <= 4) {
      return `${limitedDigits.slice(0, 2)}/${limitedDigits.slice(2)}`;
    }

    return `${limitedDigits.slice(0, 2)}/${limitedDigits.slice(2, 4)}/${limitedDigits.slice(4)}`;
  }, []);

  // ============================================================================
  // Current Date State (derived from controlled or uncontrolled value)
  // ============================================================================
  const currentDate = useMemo(() => {
    if (isControlled) {
      if (!controlledValue) return null;

      return typeof controlledValue === 'string' ? new Date(controlledValue) : controlledValue;
    }

    return uncontrolledDate;
  }, [isControlled, controlledValue, uncontrolledDate]);

  // ============================================================================
  // Internal Value State
  // ============================================================================
  const [internalValue, setInternalValue] = useState<string>(() => formatDateValue(currentDate));
  const lastExternalValueRef = useRef<string>(formatDateValue(currentDate));

  // ============================================================================
  // Validation State
  // ============================================================================
  const [hasBeenBlurred, setHasBeenBlurred] = useState(false);
  const [previousHadValue, setPreviousHadValue] = useState(() => {
    if (defaultValue) {
      const initialDate = typeof defaultValue === 'string' ? new Date(defaultValue) : defaultValue;

      return !isNaN(initialDate.getTime());
    }

    return false;
  });

  const isEmpty = useMemo(() => {
    return !currentDate || isNaN(currentDate.getTime());
  }, [currentDate]);

  /**
   * Validates if the date is within the allowed range
   */
  const isDateInRange = useCallback(
    (date: Date | null): boolean => {
      if (!date || isNaN(date.getTime())) return true;
      if (fromDate && date < fromDate) return false;
      if (toDate && date > toDate) return false;

      return true;
    },
    [fromDate, toDate],
  );

  /**
   * Checks if the current input value represents an invalid date format
   * Only checked after blur
   */
  const hasInvalidDateFormat = useMemo(() => {
    if (!hasBeenBlurred) return false;
    if (!internalValue || internalValue.trim().length === 0) return false;

    const hasSlashes = internalValue.includes('/');
    if (!hasSlashes) return false;

    const parts = internalValue.split('/');
    if (parts.length !== 3) return false;
    if (parts[2].length < 4) return false;

    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(month) || isNaN(day) || isNaN(year)) return false;
    if (month < 1 || month > 12) return true;
    if (day < 1 || day > 31) return true;
    if (year < 1000 || year > 9999) return true;

    const parsedDate = parseDateString(internalValue);
    if (!parsedDate || isNaN(parsedDate.getTime())) return true;

    return false;
  }, [internalValue, parseDateString, hasBeenBlurred]);

  const validationErrorMessage = useMemo(() => {
    if (externalErrorMessage) {
      return externalErrorMessage;
    }

    if (!hasBeenBlurred) {
      return undefined;
    }

    if (hasInvalidDateFormat) {
      return 'Please enter a valid date in MM/DD/YYYY format';
    }

    if (currentDate && !isNaN(currentDate.getTime()) && !isDateInRange(currentDate)) {
      // Show both min and max dates when date is out of range
      if (fromDate && toDate) {
        return `Date must be between ${formatDateValue(fromDate)} and ${formatDateValue(toDate)}`;
      }
      if (fromDate && currentDate < fromDate) {
        return `Date must be on or after ${formatDateValue(fromDate)}`;
      }
      if (toDate && currentDate > toDate) {
        return `Date must be on or before ${formatDateValue(toDate)}`;
      }
    }

    if (required && isEmpty) {
      return requiredErrorMessage;
    }

    return undefined;
  }, [
    required,
    isEmpty,
    hasBeenBlurred,
    externalErrorMessage,
    requiredErrorMessage,
    hasInvalidDateFormat,
    currentDate,
    isDateInRange,
    fromDate,
    toDate,
    formatDateValue,
  ]);

  // ============================================================================
  // Calendar State
  // ============================================================================
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // ============================================================================
  // Event Handlers
  // ============================================================================
  // Ref to store the target cursor position after formatting
  // Removed targetCursorPositionRef - cursor position is now managed synchronously

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const rawValue = input.value;
      const cursorPosition = input.selectionStart || 0;

      const formattedValue = formatDateInput(rawValue, previousInputValueRef.current);
      const previousLength = previousInputValueRef.current.length;
      const newLength = formattedValue.length;
      previousInputValueRef.current = formattedValue;

      // Calculate new cursor position
      let newCursorPosition = cursorPosition;
      if (newLength > previousLength) {
        // Slash was auto-inserted, move cursor past it
        newCursorPosition = cursorPosition + (newLength - previousLength);
      } else if (newLength < previousLength) {
        // Character was deleted, adjust cursor position
        newCursorPosition = Math.min(cursorPosition, newLength);
      }

      // For uncontrolled input, directly update the DOM
      // This prevents cursor jumping issues
      input.value = formattedValue;
      input.setSelectionRange(newCursorPosition, newCursorPosition);

      // Update React state for validation purposes
      setInternalValue(formattedValue);

      // Clear the blur state when user starts typing again
      // This prevents validation errors from showing while typing
      if (hasBeenBlurred) {
        setHasBeenBlurred(false);
      }

      // Only parse the date if we have a complete date (MM/DD/YYYY format)
      // Don't try to parse incomplete inputs like "0" or "01/"
      const parsedDate = formattedValue.length === 10 ? parseDateString(formattedValue) : null;
      const hasValue = parsedDate !== null && !isNaN(parsedDate.getTime());

      setPreviousHadValue((prev) => {
        if (prev) return prev;
        if (hasValue) return true;

        return false;
      });

      // Always pass the formatted string, but only pass a valid date if complete
      if (isControlled) {
        onDateChange?.(parsedDate, formattedValue);
      } else {
        setUncontrolledDate(parsedDate);
        onDateChange?.(parsedDate, formattedValue);
      }
    },
    [formatDateInput, parseDateString, isControlled, onDateChange, hasBeenBlurred],
  );

  const handleInputBlur = useCallback(() => {
    if (!validateOnBlur) {
      if (onBlur) {
        onBlur();
      }

      return;
    }

    setHasBeenBlurred(true);

    // Format the date if valid
    const parsedDate = parseDateString(internalValue);
    if (parsedDate && !isNaN(parsedDate.getTime())) {
      const formatted = formatDateValue(parsedDate);
      setInternalValue(formatted);

      if (isControlled) {
        onDateChange?.(parsedDate, formatted);
      } else {
        setUncontrolledDate(parsedDate);
        onDateChange?.(parsedDate, formatted);
      }
    }

    if (onBlur) {
      onBlur();
    }
  }, [validateOnBlur, internalValue, parseDateString, formatDateValue, isControlled, onDateChange, onBlur]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const input = e.currentTarget;
      const cursorPosition = input.selectionStart || 0;
      const currentValue = input.value;

      // Handle Backspace key when cursor is right after a slash
      if (e.key === 'Backspace' && cursorPosition > 0 && currentValue[cursorPosition - 1] === '/') {
        e.preventDefault();

        // Delete the digit before the slash, not the slash itself
        const beforeSlash = currentValue.substring(0, cursorPosition - 2);
        const afterSlash = currentValue.substring(cursorPosition);
        const newValue = beforeSlash + afterSlash;
        const digitsOnly = newValue.replace(/\D/g, '');

        let formattedValue = '';
        if (digitsOnly.length === 0) {
          formattedValue = '';
        } else if (digitsOnly.length <= 2) {
          formattedValue = digitsOnly;
        } else if (digitsOnly.length <= 4) {
          formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
        } else {
          formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
        }

        const newCursorPosition = Math.max(0, cursorPosition - 2);

        // Update the input directly for uncontrolled mode
        previousInputValueRef.current = formattedValue;
        input.value = formattedValue;
        input.setSelectionRange(newCursorPosition, newCursorPosition);

        // Update React state for validation
        setInternalValue(formattedValue);

        // Clear the blur state when user starts typing/deleting again
        if (hasBeenBlurred) {
          setHasBeenBlurred(false);
        }

        // Only parse the date if we have a complete date (MM/DD/YYYY format)
        const parsedDate = formattedValue.length === 10 ? parseDateString(formattedValue) : null;
        const hasValue = parsedDate !== null && !isNaN(parsedDate.getTime());

        setPreviousHadValue((prev) => {
          if (prev) return prev;
          if (hasValue) return true;

          return false;
        });

        if (isControlled) {
          onDateChange?.(parsedDate, formattedValue);
        } else {
          setUncontrolledDate(parsedDate);
          onDateChange?.(parsedDate, formattedValue);
        }
      }

      // Handle Delete key when cursor is right before a slash
      if (e.key === 'Delete' && cursorPosition < currentValue.length && currentValue[cursorPosition] === '/') {
        e.preventDefault();

        // Delete the digit after the slash, not the slash itself
        const beforeSlash = currentValue.substring(0, cursorPosition);
        const afterSlash = currentValue.substring(cursorPosition + 2);
        const newValue = beforeSlash + afterSlash;
        const digitsOnly = newValue.replace(/\D/g, '');

        let formattedValue = '';
        if (digitsOnly.length === 0) {
          formattedValue = '';
        } else if (digitsOnly.length <= 2) {
          formattedValue = digitsOnly;
        } else if (digitsOnly.length <= 4) {
          formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
        } else {
          formattedValue = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
        }

        const newCursorPosition = Math.min(cursorPosition, formattedValue.length);

        // Update the input directly for uncontrolled mode
        previousInputValueRef.current = formattedValue;
        input.value = formattedValue;
        input.setSelectionRange(newCursorPosition, newCursorPosition);

        // Update React state for validation
        setInternalValue(formattedValue);

        // Clear the blur state when user starts typing/deleting again
        if (hasBeenBlurred) {
          setHasBeenBlurred(false);
        }

        // Only parse the date if we have a complete date (MM/DD/YYYY format)
        const parsedDate = formattedValue.length === 10 ? parseDateString(formattedValue) : null;
        const hasValue = parsedDate !== null && !isNaN(parsedDate.getTime());

        setPreviousHadValue((prev) => {
          if (prev) return prev;
          if (hasValue) return true;

          return false;
        });

        if (isControlled) {
          onDateChange?.(parsedDate, formattedValue);
        } else {
          setUncontrolledDate(parsedDate);
          onDateChange?.(parsedDate, formattedValue);
        }
      }
    },
    [parseDateString, isControlled, onDateChange, hasBeenBlurred],
  );

  const handleToggleCalendar = useCallback(() => {
    setIsCalendarOpen((prev) => {
      if (prev) {
        restoreFocusTargetRef.current = 'button';
      }

      return !prev;
    });
  }, []);

  const handleCloseCalendar = useCallback(() => {
    restoreFocusTargetRef.current = 'button';
    setIsCalendarOpen(false);
  }, []);

  const handleSelectDateFromCalendar = useCallback(
    (date: Date) => {
      const formatted = formatDateValue(date);
      setInternalValue(formatted);
      setHasBeenBlurred(true);

      if (isControlled) {
        onDateChange?.(date, formatted);
      } else {
        setUncontrolledDate(date);
        onDateChange?.(date, formatted);
      }

      restoreFocusTargetRef.current = 'input';
      setIsCalendarOpen(false);
    },
    [formatDateValue, isControlled, onDateChange],
  );

  // ============================================================================
  // Effect: Sync internal value with external controlled value
  // ============================================================================
  useEffect(() => {
    // In controlled mode, use the string value directly from the parent
    // But only update if it's different from what we just sent to the parent
    if (isControlled && controlledValue !== undefined) {
      const newValue = typeof controlledValue === 'string' ? controlledValue : formatDateValue(controlledValue);

      // Only sync if the parent's value is different from what we last sent
      // AND different from what's currently in the input
      // This prevents overwriting user input during typing
      if (newValue !== lastExternalValueRef.current && inputRef.current && newValue !== inputRef.current.value) {
        setInternalValue(newValue);
        lastExternalValueRef.current = newValue;
        previousInputValueRef.current = newValue;
        inputRef.current.value = newValue;
      }
    } else {
      // In uncontrolled mode, format the date
      const newValue = formatDateValue(currentDate);
      if (newValue !== lastExternalValueRef.current) {
        setInternalValue(newValue);
        lastExternalValueRef.current = newValue;
        previousInputValueRef.current = newValue;

        if (inputRef.current && inputRef.current.value !== newValue) {
          inputRef.current.value = newValue;
        }
      }
    }
  }, [isControlled, controlledValue, currentDate, formatDateValue]);

  // Note: Cursor position is now managed synchronously in handleInputChange
  // No need for an effect to apply it after re-render

  // ============================================================================
  // Effect: Track if field has ever had a value
  // ============================================================================
  useEffect(() => {
    if (currentDate && !previousHadValue) {
      setPreviousHadValue(true);
    }
  }, [currentDate, previousHadValue]);

  // ============================================================================
  // Effect: Restore focus after the calendar closes
  // ============================================================================
  useEffect(() => {
    if (isCalendarOpen) return;

    const target = restoreFocusTargetRef.current;
    restoreFocusTargetRef.current = null;

    if (target === 'input') {
      inputRef.current?.focus();
    } else if (target === 'button') {
      calendarButtonRef.current?.focus();
    }
  }, [isCalendarOpen]);

  // ============================================================================
  // Effect: Close calendar on outside click
  // ============================================================================
  useEffect(() => {
    if (!isCalendarOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        calendarButtonRef.current &&
        !calendarButtonRef.current.contains(target)
      ) {
        handleCloseCalendar();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleCloseCalendar, isCalendarOpen]);

  return (
    <div
      ref={wrapperRef}
      className={`date-picker ${validationErrorMessage ? 'date-picker--error' : ''} ${className || ''}`}
      data-testid={baseTestId}>
      <div
        className='date-picker__label-wrapper'
        data-testid={`${baseTestId}-label`}>
        <label
          className='ds-c-label'
          htmlFor={inputId}>
          <span className='date-picker__label-content'>
            <span id={labelTextId}>{label}</span>
            {(required || tooltipText) && (
              <span className='date-picker__label-suffix'>
                {required && (
                  <>
                    <span
                      aria-hidden='true'
                      className='date-picker__required-asterisk'>
                      *
                    </span>
                    <RequiredStateText id={requiredTextId} />
                  </>
                )}
                {tooltipText && (
                  <Tooltip
                    className='date-picker__info-tooltip'
                    id={tooltipId}
                    placement='top'
                    title={tooltipText}>
                    <InfoIcon size={18} />
                  </Tooltip>
                )}
              </span>
            )}
          </span>
        </label>
      </div>
      {hint && <div className='ds-c-field__hint'>{hint}</div>}
      <div
        className='date-picker__input-row'
        data-testid={`${baseTestId}-input-row`}>
        <div
          className='ds-c-single-input-date-field__field-wrapper'
          data-testid={`${baseTestId}-field`}
          style={{ display: 'inline-flex', position: 'relative' }}>
          <input
            ref={inputRef}
            aria-describedby={validationErrorMessage ? errorTooltipId : undefined}
            aria-invalid={validationErrorMessage ? 'true' : 'false'}
            aria-labelledby={label ? (required ? `${labelTextId} ${requiredTextId}` : labelTextId) : undefined}
            aria-required={required ? 'true' : 'false'}
            autoComplete='off'
            className={`ds-c-field ds-c-text-input ${validationErrorMessage ? 'ds-c-text-input--error' : ''}`}
            data-testid={`${baseTestId}-input`}
            defaultValue={internalValue}
            disabled={disabled}
            id={inputId}
            name={stableName}
            placeholder={placeholder}
            required={required}
            type='text'
            onBlur={handleInputBlur}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <button
            ref={calendarButtonRef}
            aria-controls={calendarId}
            aria-expanded={isCalendarOpen}
            aria-haspopup='dialog'
            aria-label={label ? `Open calendar for ${label}` : 'Open calendar'}
            className='date-picker__calendar-button'
            data-testid={`${baseTestId}-calendar-button`}
            disabled={disabled}
            type='button'
            onClick={handleToggleCalendar}>
            <svg
              aria-hidden='true'
              fill='none'
              focusable='false'
              height='20'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              width='20'
              xmlns='http://www.w3.org/2000/svg'>
              <rect
                height='18'
                rx='2'
                ry='2'
                width='18'
                x='3'
                y='4'
              />
              <line
                x1='16'
                x2='16'
                y1='2'
                y2='6'
              />
              <line
                x1='8'
                x2='8'
                y1='2'
                y2='6'
              />
              <line
                x1='3'
                x2='21'
                y1='10'
                y2='10'
              />
            </svg>
          </button>
          {isCalendarOpen && (
            <div
              className='date-picker__calendar-popover'
              data-testid={`${baseTestId}-calendar`}>
              <Calendar
                fromDate={fromDate}
                id={calendarId}
                selectedDate={currentDate}
                toDate={toDate}
                onClose={handleCloseCalendar}
                onSelectDate={handleSelectDateFromCalendar}
              />
            </div>
          )}
        </div>

        <div
          className='date-picker__error-icon-wrapper'
          data-testid={`${baseTestId}-error`}>
          {validationErrorMessage ? (
            <>
              <span
                className='sr-only'
                id={errorTooltipId}>
                {validationErrorMessage}
              </span>
              <Tooltip
                isError
                activeClassName='date-picker__error-tooltip--active'
                className='date-picker__error-icon'
                placement='top'
                title={validationErrorMessage}>
                <span
                  className='date-picker__error-icon-content'
                  role='img'>
                  <ErrorIcon ariaLabel={`Error: ${validationErrorMessage}`} />
                </span>
              </Tooltip>
            </>
          ) : (
            <span
              aria-hidden='true'
              className='date-picker__error-icon-placeholder'
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
